import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDischargeSummaries, type DischargeSummaryListItem } from "@/lib/queries";
import { useDeleteDischargeSummary } from "@/lib/mutations";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Pencil, Trash, FileText } from "lucide-react";
import { fmtDateTime } from "@emr/shared/india";
import { CloudBadge } from "@/components/StatusBadge";
import { toast } from "sonner";

export default function DischargeSummariesPage() {
  const navigate = useNavigate();
  const { isAdmin, permissions } = useAuth();
  const canCreate = isAdmin || permissions.has("dischargesummaries.create");
  const canDelete = isAdmin || permissions.has("dischargesummaries.delete");
  const { data, isLoading } = useDischargeSummaries();
  const deleteMut = useDeleteDischargeSummary();
  const rows = data?.rows ?? [];

  const onDelete = (r: DischargeSummaryListItem) => {
    // eslint-disable-next-line no-alert -- lightweight inline confirm
    if (window.confirm(`Delete this discharge summary for ${r.patientName ?? "this patient"}? This also removes the cloud PDF copy.`)) {
      deleteMut.mutate(r.id, {
        onError: () => toast.error("Failed to delete"),
      });
    }
  };

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">Discharge Summaries</h1>
        {canCreate && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/discharge-summaries/templates")}>
              <FileText size={16} className="mr-1" /> Templates
            </Button>
            <Button className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={() => navigate("/discharge-summaries/new")}>
              <Plus size={16} className="mr-1" /> New Summary
            </Button>
          </div>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-cream-100 text-maroon-700 dark:bg-dark-700 dark:text-amber-500">
                <TableHead>Patient</TableHead>
                <TableHead>UHID</TableHead>
                <TableHead>Heading</TableHead>
                <TableHead>Admission</TableHead>
                <TableHead>Discharge</TableHead>
                <TableHead>Diagnosis</TableHead>
                <TableHead>Cloud</TableHead>
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={8}><Skeleton className="h-10" /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-sm text-muted py-8">
                    No discharge summaries yet
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow
                    key={r.id}
                    className="even:bg-cream-50/50 cursor-pointer"
                    onClick={() => navigate(`/discharge-summaries/${r.id}`)}
                  >
                    <TableCell className="font-medium">{r.patientName ?? "—"}</TableCell>
                    <TableCell className="text-xs">{r.clinicUhid ?? "—"}</TableCell>
                    <TableCell>
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${
                        r.heading === "Discharge Against Medical Advice"
                          ? "bg-red-100 text-red-800 border border-red-300"
                          : r.heading === "Discharge At Request"
                            ? "bg-amber-100 text-amber-800 border border-amber-300"
                            : "bg-cream-200 text-maroon-800 border border-cream-300"
                      }`}>
                        {r.heading}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs">{r.admissionAt ? fmtDateTime(r.admissionAt) : "—"}</TableCell>
                    <TableCell className="text-xs">{r.dischargeAt ? fmtDateTime(r.dischargeAt) : "—"}</TableCell>
                    <TableCell className="max-w-48 truncate text-xs text-muted" title={r.diagnosis ?? ""}>{r.diagnosis ?? "—"}</TableCell>
                    <TableCell>{r.savedToR2 ? <CloudBadge /> : <span className="text-xs text-muted">—</span>}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => navigate(`/discharge-summaries/${r.id}`)}>
                          <Pencil size={14} />
                        </Button>
                        {canDelete && (
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => onDelete(r)}>
                            <Trash size={14} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
